import {
  CLOAK_NO_DETECTION_CAPTION,
  DAMAGE_SCALAR_PER_DEFENSE_POINT,
  EHP_CAPTION,
} from "@sins2-atlas/compute";
import type { ExoticPrice } from "@sins2-atlas/game-data";
import { dataset, EHP_GATED, exoticName } from "./dataset";
import { priceParts } from "./format";

// The derived columns' "how is this computed" disclosure, keyed by the stat id that
// lib/stat-model.ts uses. Read by all three stat surfaces (the roster's header tooltips and
// its explainer list, the entity page's row labels, the comparison's rail) — which is why it
// is here and not inside any one of them.
//
// Formulas are the PRD's decided weapon-shape rules; captions are the ADR 0003 assumptions
// that apply to the whole column. Only the roster's explainer list renders captions; the
// other surfaces show the formula as a tooltip. Each states its fact and stops (user,
// 2026-09-08): the Corruption sum rule is said once, in ROSTER_STATS_NOTE, not per cell.
export const DERIVED_COLUMN_DISCLOSURES: {
  id: string;
  header: string;
  sub?: string;
  formula: string;
  caption?: string;
}[] = [
  {
    // First, as Game Order puts the row (ADR 0013: the game prints Cloaking above every stat
    // row). Not a formula: the quality is the cloak ability's own value at the level in force.
    // The disclosure earns its place by naming the assumption the cell's caption makes and by
    // saying why most cloakers read nothing until the reader has picked something — the row is
    // the first on the site whose state a selection decides.
    id: "cloaking",
    header: dataset.cloakLabels.cloaking,
    formula: "the rung the ship's cloak reaches, with the game's quality number beside it",
    caption:
      `Read ${CLOAK_NO_DETECTION_CAPTION}. Some ships cloak only after a research subject or ` +
      'a ship item, and read "cannot cloak" until then.',
  },
  {
    // Not a formula: the count is read straight off the structure file. The disclosure
    // earns its place by naming the two slot kinds the row's label switches between and
    // the one case that has no count (Issue 29, CONTEXT.md: Orbit Slot).
    id: "slotCost",
    header: "Orbit Slot Cost",
    formula: "the orbit slots the structure occupies; the label says Civilian or Military",
    caption: "Starbases occupy no slot.",
  },
  {
    id: "dps",
    header: "DPS",
    formula: "damage ÷ cooldown, summed over the unit's weapon mounts",
    caption:
      "Weapons that need a ship item are not counted; the ships concerned carry a note. Burst " +
      "weapons count one volley per cooldown. Torpedoes count their full damage.",
  },
  {
    id: "novaDamage",
    header: "Nova Damage",
    formula: "one volley's damage, summed over the unit's weapon mounts",
    caption: "Same weapons and rules as DPS.",
  },
  {
    id: "dpsPerSupply",
    header: "DPS/supply",
    sub: "per supply",
    formula: "unit DPS ÷ supply cost",
    caption: "Uses the DPS shown.",
  },
  {
    id: "dpsPerCost",
    header: "DPS/cost",
    sub: "per 1,000 cost",
    formula: "unit DPS ÷ (credits + metal + crystal + exotic fabrication) × 1,000",
    caption: "Exotics count at their fabrication price; the ships concerned carry a note.",
  },
  {
    id: "costPerHp",
    header: "Cost/HP",
    sub: "per raw HP",
    formula: "(credits + metal + crystal + exotic fabrication) ÷ (hull + armor + shields)",
    caption:
      "Raw pools, with no mitigation; EHP applies it. Exotics count at their fabrication price.",
  },
  {
    // Not a formula: the pool is read straight off the unit file. Decay rate and delay are on
    // the unit's Corruption card (Issue 20), not here (2026-09-08: the atlas presents data,
    // it does not teach how a pool fills).
    id: "corruption",
    header: "Corruption",
    formula: "the unit's Corruption pool size",
    caption:
      "Strikecraft and Envoys have no pool. Its thresholds are where Mild, Moderate and " +
      "Extreme Corruption begin.",
  },
  {
    // Not a formula either: the pool is a level row plus research, and the caption says only
    // that both move it (PRD story 15, Issue 50).
    id: "antimatter",
    header: "Antimatter",
    formula: "the unit's antimatter pool at the level shown",
    caption: "Level and research both raise it.",
  },
  {
    // The sub-header is compute's own caption for the cell, and the scalar in the formula is
    // the constant compute folds by — validated against the game file at extraction — so the
    // disclosure cannot drift from the arithmetic it describes (review 2026-09-08).
    id: "ehp",
    header: "EHP",
    sub: EHP_CAPTION,
    formula:
      `every pool × (1 + ${DAMAGE_SCALAR_PER_DEFENSE_POINT} × Durability); the armor pool ` +
      `also × (1 + ${DAMAGE_SCALAR_PER_DEFENSE_POINT} × Armor Strength)`,
    caption:
      "Total damage absorbed before dying, against 0 Pierce." +
      (EHP_GATED
        ? " Shows ≈ until the formula is confirmed against an in-game damage reading."
        : ""),
  },
  {
    id: "range",
    header: "Range",
    formula: "the longest weapon-mount range (planet-bombing and zero-damage weapons excluded)",
  },
  {
    id: "bombingRate",
    header: "Bombing",
    sub: "dmg/s",
    formula: "bombing damage ÷ cooldown, summed over planet-bombing weapons",
  },
  {
    id: "populationRate",
    header: "Pop. dmg",
    sub: "/s",
    formula: "population damage ÷ cooldown, summed over planet-bombing weapons",
  },
];

export const disclosureById = new Map(DERIVED_COLUMN_DISCLOSURES.map((d) => [d.id, d]));

// The sentences the two per-hull sheets show beside a fact that is not a stat cell. The entity
// page's rows and the comparison's fact rows say each of them word for word, so each is
// declared once here (review 2026-09-08: every one was spelled out on both pages).

/** The Virtual supply row's tooltip. */
export const VIRTUAL_SUPPLY_NOTE =
  "The game's threat-accounting weight on units that are not fleet-built. It is separate " +
  "from fleet supply.";

/** The Ability Points panel's Assumption Caption (ADR 0015 decision 3, its one open gate).
 * The pool's size is the single assumed thing about this lever — the arithmetic under a
 * bought Ability Level is the verified one — so the caption sits where the reader is about
 * to act on it and says what is assumed and that it is unread, in the register every other
 * Assumption Caption uses: it states the frame the figure is true under and stops. When the
 * pool is read in game, this sentence and abilityPointPool change and nothing else does. */
export const ABILITY_POINTS_POOL_CAPTION =
  "Assumed, not yet read in game: a hull has one ability point per Level.";

/** The Squadrons row's tooltip. */
export const SQUADRONS_NOTE =
  "The game's Strikecraft Squadrons value at the Level shown, plus what an equipped ship item " +
  "or a bought ability level adds. Each squadron slot fields one craft type.";

/** One craft kind's line under the Squadrons figure: the most of that craft the hull can field,
 * and whether research gates it. With the kind's name where the line is printed as prose (the
 * comparison's cell, the roster's expanded row); without it on the entity page, whose row label
 * already names the kind. */
export function craftCountText(
  option: { maxCraft: number; researchGated?: boolean },
  name?: string,
): string {
  const gate = option.researchGated ? " (needs research)" : "";
  return name === undefined
    ? `up to ${option.maxCraft}${gate}`
    : `up to ${option.maxCraft}× ${name}${gate}`;
}

/** One exotic component's tooltip: what it is, and what the factory charges to build one —
 * the entity page's Cost rows and its item shop both say it. */
export function exoticComponentNote(exotic: Pick<ExoticPrice, "exoticType" | "fabrication">) {
  return (
    `An exotic component. The exotic factory builds one ${exoticName(exotic.exoticType)} for ` +
    `${priceParts(exotic.fabrication).join(", ")}.`
  );
}
